import React from 'react';
import { CTASection } from '../../components/CTASection';
import { Zap, CheckCircle, Sparkles, Award, Layers, ArrowRight } from 'lucide-react';
export function LaserEmbroidery() {
  const benefits = [{
    icon: <Sparkles className="w-6 h-6" data-id="element-1583" />,
    title: 'Precision & Detail',
    description: 'Intricate designs with clean, precise edges impossible by hand'
  }, {
    icon: <Zap className="w-6 h-6" data-id="element-1584" />,
    title: 'Fast & Efficient',
    description: 'Modernize traditional methods—no time-consuming scissors or hand cutting'
  }, {
    icon: <Award className="w-6 h-6" data-id="element-1585" />,
    title: 'Superior Quality',
    description: 'Enriched embroidery technology that adds value to every piece'
  }, {
    icon: <Layers className="w-6 h-6" data-id="element-1586" />,
    title: 'Versatile Applications',
    description: 'Normal and reverse appliqué on any fabric, material, or textile'
  }];
  const advantages = ['Minimum product damage potential', 'No or fewer consumables required', 'No toxic by-product disposal', 'Simple operation and maintenance', 'Ideal for mass production', 'Works on various fabrics and materials'];
  const applications = [{
    title: 'Unique Embroidery',
    description: 'Create intricate, one-of-a-kind embroidered designs'
  }, {
    title: 'Appliqué Work',
    description: 'Normal and reverse appliqué with precision cutting'
  }, {
    title: 'Engraving',
    description: 'Add texture and depth to fabric surfaces'
  }, {
    title: 'Edge Cutting',
    description: 'Clean, professional edges on embroidered pieces'
  }, {
    title: 'Punching',
    description: 'Precise perforations for decorative effects'
  }, {
    title: 'Stratified Cutting',
    description: 'Multi-layer cutting for complex designs'
  }];
  return <div className="flex flex-col w-full" data-id="element-1587">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-20" data-id="element-1588">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1589">
          <div className="flex justify-center mb-6" data-id="element-1590">
            <Zap className="w-16 h-16 text-white" data-id="element-1591" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1592">
            Laser Embroidery
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-1593">
            Where precision meets artistry. Modern laser technology transforms
            traditional embroidery with intricate detail, speed, and unmatched
            quality.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white" data-id="element-1594">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1595">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center" data-id="element-1596">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-1597">
            {/* Phase 1 */}
            <div className="relative" data-id="element-1598">
              <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-2xl p-8 text-white text-center h-full" data-id="element-1599">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-wh-pink text-2xl font-bold mx-auto mb-4" data-id="element-1600">
                  1
                </div>
                <h3 className="text-2xl font-bold mb-3" data-id="element-1601">Embroidery</h3>
                <p className="text-white/90" data-id="element-1602">
                  Your design is embroidered onto the fabric using our
                  state-of-the-art embroidery machines.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10" data-id="element-1603">
                <ArrowRight className="w-8 h-8 text-wh-pink" data-id="element-1604" />
              </div>
            </div>

            {/* Phase 2 */}
            <div className="relative" data-id="element-1605">
              <div className="bg-gradient-to-br from-wh-teal to-teal-600 rounded-2xl p-8 text-white text-center h-full" data-id="element-1606">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-wh-teal text-2xl font-bold mx-auto mb-4" data-id="element-1607">
                  2
                </div>
                <h3 className="text-2xl font-bold mb-3" data-id="element-1608">Laser Cut</h3>
                <p className="text-white/90" data-id="element-1609">
                  Precision laser technology cuts away excess fabric with
                  incredible accuracy and clean edges.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10" data-id="element-1610">
                <ArrowRight className="w-8 h-8 text-wh-teal" data-id="element-1611" />
              </div>
            </div>

            {/* Phase 3 */}
            <div data-id="element-1612">
              <div className="bg-gradient-to-br from-wh-yellow to-yellow-500 rounded-2xl p-8 text-gray-900 text-center h-full" data-id="element-1613">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-wh-yellow text-2xl font-bold mx-auto mb-4" data-id="element-1614">
                  3
                </div>
                <h3 className="text-2xl font-bold mb-3" data-id="element-1615">Pull Off</h3>
                <p className="text-gray-800" data-id="element-1616">
                  The cut fabric is removed, revealing your intricate,
                  professional laser-embroidered design.
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-4xl mx-auto mb-16" data-id="element-1617">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1618">
              Modern Laser Technology Meets Traditional Craft
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4" data-id="element-1619">
              <p data-id="element-1620">
                Laser technology has revolutionized the apparel industry,
                offering significant advantages over conventional processes in
                cutting, embossing, denim fading, and more. At Wear Haus
                Designs, we use the most renowned modern laser systems—simple to
                operate, easy to maintain, and capable of delivering
                extraordinary results.
              </p>
              <p data-id="element-1621">
                Our laser cutting machines are suitable for various fabrics and
                excel at unique embroidery, appliqué, engraving, edge-cutting,
                punching, and stratified cutting. They don't just enrich
                embroidery technology—they{' '}
                <span className="font-bold text-wh-pink" data-id="element-1622">
                  improve product quality while adding value
                </span>{' '}
                to every piece.
              </p>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-1623">
            {benefits.map((benefit, index) => <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1624">
                <div className="flex items-start gap-4" data-id="element-1625">
                  <div className="flex-shrink-0 w-12 h-12 bg-wh-pink rounded-lg flex items-center justify-center text-white" data-id="element-1626">
                    {benefit.icon}
                  </div>
                  <div data-id="element-1627">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1628">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-1629">{benefit.description}</p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Applications */}
          <div className="mb-16" data-id="element-1630">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1631">
              What We Can Create
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1632">
              {applications.map((application, index) => <div key={index} className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-wh-teal transition-colors" data-id="element-1633">
                  <h3 className="text-xl font-bold text-wh-teal mb-2" data-id="element-1634">
                    {application.title}
                  </h3>
                  <p className="text-gray-700" data-id="element-1635">{application.description}</p>
                </div>)}
            </div>
          </div>

          {/* Advantages */}
          <div className="bg-gradient-to-br from-wh-teal to-teal-600 rounded-2xl p-12 text-white mb-16" data-id="element-1636">
            <h2 className="text-3xl font-bold mb-8 text-center" data-id="element-1637">
              Why Laser Embroidery?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-id="element-1638">
              {advantages.map((advantage, index) => <div key={index} className="flex items-center gap-3" data-id="element-1639">
                  <CheckCircle className="w-6 h-6 text-wh-yellow flex-shrink-0" data-id="element-1640" />
                  <span className="text-lg" data-id="element-1641">{advantage}</span>
                </div>)}
            </div>
          </div>

          {/* Appliqué Highlight */}
          <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-2xl p-12 text-center text-white" data-id="element-1642">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1643">
              Perfect for Appliqué Work
            </h2>
            <p className="text-xl text-white/90 mb-6 max-w-3xl mx-auto" data-id="element-1644">
              Our laser cutting machines conveniently handle normal and reverse
              appliqué. Incredibly simple, fast, and effective, they modernize
              traditional appliqué methods without time-consuming scissors or
              hand cutting. Ideal for mass production of laser-cut appliqués on
              any fabric, material, or textile.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1645">
              <a href="/Contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-wh-pink font-bold rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1646">
                Get a Quote
              </a>
              <a href="/services" className="inline-flex items-center justify-center px-8 py-4 bg-wh-yellow text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors" data-id="element-1647">
                View All Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection data-id="element-1648" />
    </div>;
}